import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetAiChatBotHistoryQuery,
  useGetPublicAiHistoryQuery,
  useSendPublicAiMessageMutation,
  useSendAiChatBotMessageMutation,
  useFlagAiChatBotMessageMutation,
  useStartNewAiChatBotConversationMutation,
  useGetMyAiChatBotConversationsQuery,
  useDeleteAiChatBotConversationMutation,
  useRestoreAiChatBotConversationMutation,
  useLazyGetAiChatBotConversationDetailQuery,
} from "../../../app/services/book/aiChatBot"
import Avatar from "../Chatbox/Avatar"
import { renderMarkdown, OfferCard } from "./AiChatParts"
import useLoadingTip from "./useLoadingTip"

const formatTime = (value) => {
  const d = value ? new Date(value) : new Date()
  if (isNaN(d.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} | ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const FlagControl = ({ message, onFlag }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(false)

  if (message.meta?.flagged) {
    return (
      <span className="text-[11px] text-slate-400 px-1">
        {t("admin.ai_chat_bot_flagged", "Reported as incorrect. Thanks!")}
      </span>
    )
  }

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="text-[11px] text-slate-400 hover:text-rose-600 px-1"
      >
        {t("admin.ai_chat_bot_flag", "Incorrect answer")}
      </button>
    )
  }

  const submit = async () => {
    setIsSaving(true)
    setError(false)
    try {
      await onFlag(message.id, comment.trim())
    } catch (_error) {
      setError(true)
      setIsSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5 px-1 mt-0.5 w-full">
      <input
        type="text"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") submit()
        }}
        placeholder={t("admin.ai_chat_bot_flag_placeholder", "What was wrong? (optional)")}
        className="flex-1 min-w-0 border border-slate-300 rounded px-2 py-1 text-xs"
        autoFocus
      />
      <button
        type="button"
        onClick={submit}
        disabled={isSaving}
        className="text-xs px-2 py-1 rounded bg-rose-600 text-white disabled:opacity-50"
      >
        {t("admin.ai_chat_bot_flag_send", "Report")}
      </button>
      <button type="button" onClick={() => setIsOpen(false)} className="text-xs text-slate-400 px-1">
        ×
      </button>
      {error && <span className="text-[11px] text-rose-600">{t("main.error_occurred", "Something went wrong.")}</span>}
    </div>
  )
}

const MessageBubble = ({ message, canFlag, onFlag }) => {
  const isBot = message.role === "bot"
  const isOwn = !isBot
  const offers =
    message.meta?.offers ||
    (Array.isArray(message.meta?.payload) ? message.meta.payload.filter((offer) => offer?.book_url) : [])

  return (
    <div
      className={`ai_chat_bot_row flex gap-2.5 mb-3 ${isOwn ? "ai_chat_bot_row_own justify-end" : "ai_chat_bot_row_bot justify-start"}`}
    >
      {!isOwn && <Avatar className="w-8 h-8 mt-4" company="AI" />}

      <div className={`flex flex-col max-w-[80%] ${isOwn ? "items-end" : "items-start"}`}>
        <span className="text-xs font-semibold text-slate-500 mb-0.5 px-1">
          {isOwn ? message.author_name || "" : "AI Bot"}
        </span>

        <div
          className={`ai_chat_bot_bubble border shadow-sm px-4 py-2.5 ${
            isOwn
              ? "ai_chat_bot_bubble_own bg-orange-50/30 border-orange-200"
              : "ai_chat_bot_bubble_bot bg-slate-50 border-slate-200"
          }`}
          style={{ borderRadius: isOwn ? "18px 4px 18px 18px" : "4px 18px 18px 18px" }}
        >
          <p className="text-[15px] leading-[1.5] whitespace-pre-wrap break-words text-slate-900">
            {isBot ? renderMarkdown(message.content || "") : message.content}
          </p>

          {offers.length > 0 && (
            <div className="mt-2 grid gap-2">
              {offers.map((offer, index) => (
                <OfferCard key={`${offer.token}-${offer.price_id}-${index}`} offer={offer} />
              ))}
            </div>
          )}
        </div>

        {message.created_at && (
          <span className="text-[11px] text-slate-400 mt-0.5 px-1">
            {formatTime(message.created_at)}
          </span>
        )}

        {isBot && canFlag && Number.isInteger(message.id) && <FlagControl message={message} onFlag={onFlag} />}
      </div>
    </div>
  )
}

const TypingIndicator = ({ reassuranceTip }) => {
  const { t } = useTranslation()

  return (
    <div className="flex gap-2.5 mb-3 justify-start">
      <Avatar className="w-8 h-8 mt-4" company="AI" />
      <div
        className="border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-2"
        style={{ borderRadius: "4px 18px 18px 18px" }}
      >
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce"
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </div>
        {reassuranceTip && (
          <span className="text-xs text-slate-500">
            {t(reassuranceTip[0], reassuranceTip[1])}
          </span>
        )}
      </div>
    </div>
  )
}

const publicTokenKey = (publicKey) => `travel_ai_${publicKey}`

const readPublicToken = (publicKey) => {
  try {
    return window.localStorage.getItem(publicTokenKey(publicKey))
  } catch (_error) {
    return null
  }
}

const writePublicToken = (publicKey, token) => {
  try {
    if (token) window.localStorage.setItem(publicTokenKey(publicKey), token)
    else window.localStorage.removeItem(publicTokenKey(publicKey))
  } catch (_error) {
    return
  }
}

const AiChatBotPage = ({ themeName, publicKey, publicTitle }) => {
  const { t } = useTranslation()
  const isPublic = Boolean(publicKey)
  const [publicToken, setPublicToken] = useState(() => (publicKey ? readPublicToken(publicKey) : null))
  const { data: staffHistory, isLoading: isStaffHistoryLoading } = useGetAiChatBotHistoryQuery(undefined, { skip: isPublic })
  const { data: publicHistory, isLoading: isPublicHistoryLoading } = useGetPublicAiHistoryQuery(
    { key: publicKey, token: publicToken },
    { skip: !isPublic || !publicToken }
  )
  const [sendPublicMessage] = useSendPublicAiMessageMutation()
  const history = staffHistory
  const isHistoryLoading = isStaffHistoryLoading || isPublicHistoryLoading
  const [showArchive, setShowArchive] = useState(false)
  const [viewingArchived, setViewingArchived] = useState(false)
  const { data: myConversations, refetch: refetchMyConversations } = useGetMyAiChatBotConversationsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    skip: isPublic,
  })
  const { data: archivedConversations, refetch: refetchArchived } = useGetMyAiChatBotConversationsQuery(
    { archived: 1 },
    { skip: isPublic || !showArchive, refetchOnMountOrArgChange: true }
  )
  const [restoreConversation, { isLoading: isRestoring }] = useRestoreAiChatBotConversationMutation()
  const [sendMessage, { isLoading: isSending }] = useSendAiChatBotMessageMutation()
  const reassuranceTip = useLoadingTip(isSending)
  const [flagMessage] = useFlagAiChatBotMessageMutation()
  const [startNewConversation, { isLoading: isStartingNew }] = useStartNewAiChatBotConversationMutation()
  const [fetchConversationDetail] = useLazyGetAiChatBotConversationDetailQuery()
  const [deleteConversation, { isLoading: isDeletingConversation }] = useDeleteAiChatBotConversationMutation()
  const [text, setText] = useState("")
  const [messages, setMessages] = useState([])
  const [conversationId, setConversationId] = useState(null)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const bottomRef = useRef(null)
  const textareaRef = useRef(null)
  const pageRef = useRef(null)
  const parallaxRafRef = useRef(0)
  const parallaxTargetRef = useRef({ x: 0, y: 0 })
  const parallaxCurrentRef = useRef({ x: 0, y: 0 })

  useEffect(() => {
    if (history?.messages) {
      setMessages(history.messages)
      setConversationId(history.conversation_id)
    }
  }, [history])

  useEffect(() => {
    if (publicHistory?.messages) {
      setMessages(
        publicHistory.messages.map((message, index) => ({
          id: `history-${index}`,
          role: message.role,
          content: message.content,
          meta: { offers: message.offers || [] },
          created_at: message.created_at,
        }))
      )
    }
  }, [publicHistory])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isSending])

  useEffect(() => {
    if (themeName !== "novatravel") return
    if (typeof window === "undefined") return

    const page = pageRef.current
    const outer = page && page.closest(".app-main__outer")
    if (!outer) return

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    const coarsePointer = window.matchMedia("(pointer: coarse)").matches

    outer.classList.add("ai_chat_bot_parallax_host")

    let atmosphere = outer.querySelector(".ai_chat_bot_atmosphere_layer")
    if (!atmosphere) {
      atmosphere = document.createElement("div")
      atmosphere.className = "ai_chat_bot_atmosphere_layer"
      atmosphere.setAttribute("aria-hidden", "true")

      const driftOrbs = [
        { top: "7%", left: "8%", size: 42, variant: 1 },
        { top: "12%", left: "22%", size: 28, variant: 2 },
        { top: "5%", left: "38%", size: 54, variant: 3 },
        { top: "16%", left: "52%", size: 24, variant: 4 },
        { top: "9%", left: "68%", size: 46, variant: 5 },
        { top: "14%", left: "84%", size: 32, variant: 6 },
        { top: "28%", left: "14%", size: 36, variant: 2 },
        { top: "34%", left: "30%", size: 22, variant: 1 },
        { top: "26%", left: "46%", size: 48, variant: 4 },
        { top: "38%", left: "62%", size: 30, variant: 3 },
        { top: "32%", left: "78%", size: 40, variant: 6 },
        { top: "48%", left: "10%", size: 26, variant: 5 },
        { top: "52%", left: "36%", size: 34, variant: 1 },
        { top: "46%", left: "58%", size: 20, variant: 2 },
        { top: "56%", left: "74%", size: 38, variant: 4 },
        { top: "62%", left: "88%", size: 24, variant: 3 },
      ]

      driftOrbs.forEach((orb) => {
        const el = document.createElement("span")
        el.className = `ai_chat_bot_drift_orb ai_chat_bot_drift_orb_${orb.variant}`
        el.style.top = orb.top
        el.style.left = orb.left
        el.style.width = `${orb.size}px`
        el.style.height = `${orb.size}px`
        atmosphere.appendChild(el)
      })

      if (!reduceMotion && !coarsePointer) {
        const cursorBlobs = [
          { top: "16%", left: "3%", size: 220, dx: "52px", dy: "30px" },
          { top: "58%", left: "1%", size: 180, dx: "40px", dy: "-36px" },
          { top: "20%", right: "2%", left: "auto", size: 200, dx: "-48px", dy: "34px" },
          { top: "62%", right: "4%", left: "auto", size: 170, dx: "-38px", dy: "-28px" },
        ]

        cursorBlobs.forEach((blob) => {
          const el = document.createElement("span")
          el.className = "ai_chat_bot_cursor_blob"
          el.style.top = blob.top
          if (blob.left === "auto") {
            el.style.left = "auto"
            el.style.right = blob.right
          } else {
            el.style.left = blob.left
          }
          el.style.width = `${blob.size}px`
          el.style.height = `${blob.size}px`
          el.style.setProperty("--ai_blob_dx", blob.dx)
          el.style.setProperty("--ai_blob_dy", blob.dy)
          atmosphere.appendChild(el)
        })
      }

      outer.insertBefore(atmosphere, outer.firstChild)
    }

    if (reduceMotion || coarsePointer) {
      return () => {
        outer.classList.remove("ai_chat_bot_parallax_host")
        if (atmosphere && atmosphere.parentNode === outer) {
          atmosphere.remove()
        }
      }
    }

    const onMove = (event) => {
      const rect = outer.getBoundingClientRect()
      if (rect.width < 1 || rect.height < 1) return
      parallaxTargetRef.current = {
        x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
        y: ((event.clientY - rect.top) / rect.height) * 2 - 1,
      }
    }

    const onLeave = () => {
      parallaxTargetRef.current = { x: 0, y: 0 }
    }

    const tick = () => {
      const current = parallaxCurrentRef.current
      const target = parallaxTargetRef.current
      current.x += (target.x - current.x) * 0.08
      current.y += (target.y - current.y) * 0.08
      outer.style.setProperty("--ai_mx", current.x.toFixed(4))
      outer.style.setProperty("--ai_my", current.y.toFixed(4))
      parallaxRafRef.current = window.requestAnimationFrame(tick)
    }

    outer.addEventListener("mousemove", onMove, { passive: true })
    outer.addEventListener("mouseleave", onLeave)
    parallaxRafRef.current = window.requestAnimationFrame(tick)

    return () => {
      outer.removeEventListener("mousemove", onMove)
      outer.removeEventListener("mouseleave", onLeave)
      window.cancelAnimationFrame(parallaxRafRef.current)
      outer.classList.remove("ai_chat_bot_parallax_host")
      outer.style.removeProperty("--ai_mx")
      outer.style.removeProperty("--ai_my")
      if (atmosphere && atmosphere.parentNode === outer) {
        atmosphere.remove()
      }
    }
  }, [themeName])

  const handleSend = async () => {
    const value = text.trim()
    if (!value || isSending) return

    setMessages((prev) => [
      ...prev,
      {
        id: `local-${Date.now()}`,
        role: "user",
        content: value,
        meta: {},
        created_at: new Date().toISOString(),
      },
    ])
    setText("")

    try {
      if (isPublic) {
        const result = await sendPublicMessage({ key: publicKey, text: value, token: publicToken }).unwrap()
        setPublicToken(result.token)
        writePublicToken(publicKey, result.token)
        setMessages((prev) => [
          ...prev,
          {
            id: `bot-${Date.now()}`,
            role: "bot",
            content: result.reply,
            meta: { offers: result.offers || [] },
            created_at: new Date().toISOString(),
          },
        ])
        return
      }

      const result = await sendMessage({ text: value, conversation_id: conversationId }).unwrap()
      setConversationId(result.conversation_id)
      setMessages((prev) => [
        ...prev,
        {
          id: result.message_id || `bot-${Date.now()}`,
          role: "bot",
          content: result.reply,
          meta: { offers: result.offers || [] },
          created_at: new Date().toISOString(),
        },
      ])
      refetchMyConversations()
    } catch (_error) {
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: "bot",
          content:
            _error?.status === 429
              ? t("admin.ai_chat_bot_rate_limited", "Too many messages. Please wait a minute and try again.")
              : t("main.error_occurred", "Something went wrong."),
          meta: {},
          created_at: new Date().toISOString(),
        },
      ])
    }
  }

  const handleFlag = async (id, comment) => {
    await flagMessage({ id, comment }).unwrap()
    setMessages((prev) =>
      prev.map((message) =>
        message.id === id ? { ...message, meta: { ...message.meta, flagged: true } } : message
      )
    )
  }

  const handleNewChat = async () => {
    if (isPublic) {
      setPublicToken(null)
      writePublicToken(publicKey, null)
      setMessages([])
      return
    }

    try {
      const result = await startNewConversation().unwrap()
      setConversationId(result.conversation_id)
      setMessages([])
      setViewingArchived(false)
      setMobileSidebarOpen(false)
      refetchMyConversations()
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleSelectConversation = async (id) => {
    if (id === conversationId) {
      setMobileSidebarOpen(false)
      return
    }
    try {
      const result = await fetchConversationDetail({ bundle_id: id }).unwrap()
      setConversationId(result.conversation_id)
      setMessages(result.messages)
      setViewingArchived(Boolean(result.archived))
      setMobileSidebarOpen(false)
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleDeleteConversation = async (event, id) => {
    event.stopPropagation()
    try {
      await deleteConversation(id).unwrap()
      refetchMyConversations()

      if (id === conversationId) {
        await handleNewChat()
      }
    } catch (_error) {
      // ignore — user can retry
    }
  }

  const handleRestoreConversation = async (event, id) => {
    event?.stopPropagation()
    try {
      await restoreConversation(id).unwrap()
      if (id === conversationId) setViewingArchived(false)
      refetchArchived()
      refetchMyConversations()
    } catch (_error) {
      return
    }
  }

  const sidebarConversations = (showArchive ? archivedConversations : myConversations)?.conversations || []

  return (
    <div
      ref={pageRef}
      className={`ai_chat_bot_page relative w-full max-w-5xl mx-auto p-3 sm:p-4 flex flex-col gap-3 min-h-[480px] md:flex-row md:gap-4 ${
        isPublic ? "h-screen" : "h-[calc(100vh-140px)] md:h-[80vh]"
      }`}
    >
      <div className="ai_chat_bot_mobile_bar flex items-center gap-2 md:hidden relative z-[1]">
        {!isPublic && (
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="ai_chat_bot_mobile_chats shrink-0 px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-medium"
          >
            {t("admin.ai_chat_bot_chats", "Chats")}
          </button>
        )}
        <h1 className="ai_chat_bot_mobile_title flex-1 min-w-0 truncate text-base font-semibold text-slate-800 m-0">
          {publicTitle || t("admin.ai_chat_bot_title", "AI Chat Search")}
        </h1>
        <button
          type="button"
          onClick={handleNewChat}
          disabled={isStartingNew}
          className="ai_chat_bot_mobile_new shrink-0 w-10 h-10 rounded-md bg-slate-800 text-white text-xl leading-none disabled:opacity-50"
          aria-label={t("admin.ai_chat_bot_new_chat", "New chat")}
        >
          +
        </button>
      </div>

      {mobileSidebarOpen && (
        <button
          type="button"
          className="ai_chat_bot_sidebar_backdrop fixed inset-0 z-[40] bg-black/30 border-0 p-0 max-md:block md:hidden"
          aria-label={t("admin.ai_chat_bot_back_to_chats", "Back to chats")}
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {!isPublic && (
      <div
        className={`ai_chat_bot_sidebar relative z-[1] w-56 shrink-0 flex flex-col border border-slate-200 rounded-lg bg-white overflow-hidden max-md:fixed max-md:z-[50] max-md:top-[60px] max-md:bottom-0 max-md:left-0 max-md:w-[min(288px,86vw)] max-md:rounded-none max-md:shadow-xl max-md:transition-transform max-md:duration-200 max-md:ease-out ${
          mobileSidebarOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full"
        }`}
      >
        <button
          type="button"
          onClick={handleNewChat}
          disabled={isStartingNew}
          className="ai_chat_bot_new_chat m-2 px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-medium disabled:opacity-50"
        >
          + {t("admin.ai_chat_bot_new_chat", "New chat")}
        </button>
        <div className="ai_chat_bot_sidebar_list flex-1 overflow-y-auto px-2 pb-2">
          {showArchive && sidebarConversations.length === 0 && (
            <div className="px-2 py-3 text-xs text-slate-400">
              {t("admin.ai_chat_bot_archive_empty", "No deleted chats")}
            </div>
          )}
          {sidebarConversations.map((conversation) => (
            <div
              key={conversation.id}
              className={`ai_chat_bot_sidebar_item group w-full flex items-center gap-1 px-2 py-2 rounded-md text-xs mb-1 ${
                conversation.id === conversationId
                  ? "ai_chat_bot_sidebar_item_active bg-blue-50 text-blue-800"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <button
                type="button"
                onClick={() => handleSelectConversation(conversation.id)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="truncate font-medium">
                  {conversation.preview || t("admin.ai_chat_bot_new_chat", "New chat")}
                </div>
                <div className="text-[10px] text-slate-400 truncate">
                  {formatTime(showArchive ? conversation.deleted_at : conversation.updated_at)}
                  {conversation.user_name && ` · ${conversation.user_name}`}
                </div>
              </button>
              {showArchive ? (
                <button
                  type="button"
                  onClick={(event) => handleRestoreConversation(event, conversation.id)}
                  disabled={isRestoring}
                  title={t("admin.ai_chat_bot_restore", "Restore")}
                  aria-label={t("admin.ai_chat_bot_restore", "Restore")}
                  className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-50"
                >
                  ↺
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(event) => handleDeleteConversation(event, conversation.id)}
                  disabled={isDeletingConversation}
                  title={t("admin.ai_chat_bot_delete_chat", "Delete")}
                  aria-label={t("admin.ai_chat_bot_delete_chat", "Delete")}
                  className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-slate-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 transition-opacity"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowArchive((value) => !value)}
          className="ai_chat_bot_archive_toggle shrink-0 border-t border-slate-200 px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700 text-left"
        >
          {showArchive
            ? `← ${t("admin.ai_chat_bot_back_to_chats", "Back to chats")}`
            : t("admin.ai_chat_bot_archive", "Deleted chats")}
        </button>
      </div>
      )}

      <div className="ai_chat_bot_main relative z-[1] flex-1 flex flex-col min-w-0 min-h-0">
        <div className="ai_chat_bot_header hidden md:flex items-center justify-between mb-3">
          <h1 className="ai_chat_bot_title text-lg font-semibold text-slate-800 m-0">
            {publicTitle || t("admin.ai_chat_bot_title", "AI Chat Search")}
          </h1>
          {isPublic && (
            <button
              type="button"
              onClick={handleNewChat}
              className="ai_chat_bot_new_chat px-3 py-2 rounded-md bg-slate-800 text-white text-sm font-medium"
            >
              + {t("admin.ai_chat_bot_new_chat", "New chat")}
            </button>
          )}
        </div>

        <div className="ai_chat_bot_messages flex-1 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-100 min-h-0">
          {isHistoryLoading ? (
            <div className="text-sm text-slate-400">{t("admin.loading_dots")}</div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                canFlag={Boolean(history?.flag_button_enabled)}
                onFlag={handleFlag}
              />
            ))
          )}
          {isSending && <TypingIndicator reassuranceTip={reassuranceTip} />}
          <div ref={bottomRef} />
        </div>

        {viewingArchived ? (
          <div className="ai_chat_bot_archived_notice mt-3 flex items-center justify-between gap-2 border border-amber-200 bg-amber-50 rounded-md px-3 py-2 text-sm text-amber-800">
            <span>{t("admin.ai_chat_bot_archived_notice", "This chat is deleted. Restore it to continue.")}</span>
            <button
              type="button"
              onClick={() => handleRestoreConversation(null, conversationId)}
              disabled={isRestoring}
              className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm disabled:opacity-50"
            >
              {t("admin.ai_chat_bot_restore", "Restore")}
            </button>
          </div>
        ) : (
          <div className="ai_chat_bot_composer mt-3 flex gap-2">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault()
                  handleSend()
                }
              }}
              rows={1}
              placeholder={t(
                "admin.ai_chat_bot_placeholder",
                'Ask for a tour, e.g. "2+1, Kemer, 7 nights"'
              )}
              className="ai_chat_bot_input flex-1 border border-slate-300 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-[15px] leading-relaxed"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending || !text.trim()}
              className="ai_chat_bot_send px-4 py-2 rounded-md bg-blue-600 text-white disabled:opacity-50"
            >
              {t("admin.send", "Send")}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default AiChatBotPage
